/**
 * KAN-891, KAN-892, KAN-887: кнопки быстрого выбора таблицы CRM как
 * переключатели (одна активна, повторный клик - вид по умолчанию) и
 * параметры адреса таблицы ?lead=1, ?idle=1, ?event= (KAN-883).
 *
 * - "Продажи": ?period=all&outcome=won;
 * - "Неразобранные": ?period=all&outcome=open&manager=none&lead=1 (без idle);
 * - "Нет движения": ?period=all&outcome=open&idle=1 (KAN-907: без lead=1, в сигнал входят
 *   и взятые клиенты, которые ни разу не писали; manager=none снимается, конкретный
 *   менеджер и me сохраняются);
 * - KAN-898 "Мусор" (только владельцу, доступность - useCrmJunkView): ?period=all&junk=1
 *   (без исхода, manager=none снимается).
 * Каждая кнопка снимает даты, сортировку, событие и чужие признаки (в том числе junk).
 * KAN-883: ручная смена периода или исхода снимает активную кнопку (withoutCrmShortcut): признаки junk,
 * lead, idle и manager=none (последний только у активной "Неразобранные"); исход и менеджер uuid/me остаются.
 * Вид по умолчанию: без period/from/to/outcome/sort/lead/idle/event/junk и без manager=none; поиск,
 * атрибут, город, организация и менеджер (uuid, me) сохраняются.
 */
import type { CrmLeadsEvent, CrmLeadsParams } from '@/types/crm';
import { CRM_FILTER_PARAM, MANAGER_FILTER_NONE } from './crmLeadFilters';
import { CRM_PERIOD_ALL, CRM_PERIOD_PARAM } from './crmTablePeriod';
import { CRM_SORT_PARAM } from './crmTableSort';

export const CRM_SHORTCUT_PARAM = { lead: 'lead', idle: 'idle', event: 'event', junk: 'junk' } as const;

export type CrmTableShortcut = 'sales' | 'unassigned' | 'idle' | 'junk';

const FLAG_ON = '1';

const CRM_LEADS_EVENTS: readonly CrmLeadsEvent[] = [
  'lead',
  'lead_replied',
  'lead_inbound',
  'sent',
  'handed',
  'taken',
  'won',
  'lead_or_taken',
];

/** Событие периода из адреса; неизвестное или пустое - null (на сервере lead_or_taken, KAN-973). */
export function parseCrmLeadsEvent(value: string | null): CrmLeadsEvent | null {
  return CRM_LEADS_EVENTS.find((e) => e === value) ?? null;
}

/** Подпись события для отметки над таблицей ("Даты по событию: ..."). */
export const CRM_LEADS_EVENT_LABEL: Record<CrmLeadsEvent, string> = {
  lead: 'появление лида',
  lead_replied: 'появление лида (ответил на рассылку)',
  lead_inbound: 'появление лида (написал сам)',
  sent: 'первое сообщение бота',
  handed: 'передача менеджеру',
  taken: 'взятие в работу',
  won: 'продажа',
  lead_or_taken: 'появление лида или взятие в работу',
};

/** События, при которых таблица за период группируется по дням period_at, как без event (KAN-973). */
const DAY_GROUPED_EVENTS: readonly CrmLeadsEvent[] = ['taken', 'lead_or_taken'];

/** Событие периода, отличное от умолчания (заголовки дней не рисуются, над таблицей отметка); иначе null. */
export function otherPeriodEvent(event: CrmLeadsEvent | undefined): CrmLeadsEvent | null {
  return event && !DAY_GROUPED_EVENTS.includes(event) ? event : null;
}

/** Признаки таблицы из адреса для запроса: lead=1, idle=1, event. */
export type CrmTableFlags = Pick<CrmLeadsParams, 'event' | 'lead' | 'idle'>;

export function parseCrmTableFlags(params: URLSearchParams): CrmTableFlags {
  const flags: CrmTableFlags = {};
  const event = parseCrmLeadsEvent(params.get(CRM_SHORTCUT_PARAM.event));
  if (event) flags.event = event;
  if (params.get(CRM_SHORTCUT_PARAM.lead) === FLAG_ON) flags.lead = FLAG_ON;
  if (params.get(CRM_SHORTCUT_PARAM.idle) === FLAG_ON) flags.idle = FLAG_ON;
  return flags;
}

/** KAN-898: в адресе открыт вид "Мусор" (?junk=1). */
export function isCrmJunkView(params: URLSearchParams): boolean {
  return params.get(CRM_SHORTCUT_PARAM.junk) === FLAG_ON;
}

/** Активная кнопка по адресу (не больше одной). */
export function activeCrmShortcut(params: URLSearchParams): CrmTableShortcut | null {
  if (params.get(CRM_PERIOD_PARAM.period) !== CRM_PERIOD_ALL) return null;
  if (isCrmJunkView(params)) return 'junk';
  const outcome = params.get(CRM_PERIOD_PARAM.outcome);
  if (outcome === 'won') return 'sales';
  if (outcome !== 'open') return null;
  if (params.get(CRM_SHORTCUT_PARAM.idle) === FLAG_ON) return 'idle';
  if (params.get(CRM_SHORTCUT_PARAM.lead) !== FLAG_ON) return null;
  return params.get(CRM_FILTER_PARAM.manager) === MANAGER_FILTER_NONE ? 'unassigned' : null;
}

function dropManagerNone(next: URLSearchParams): void {
  if (next.get(CRM_FILTER_PARAM.manager) === MANAGER_FILTER_NONE) next.delete(CRM_FILTER_PARAM.manager);
}

const RESET_KEYS = [
  CRM_PERIOD_PARAM.period,
  CRM_PERIOD_PARAM.from,
  CRM_PERIOD_PARAM.to,
  CRM_PERIOD_PARAM.outcome,
  CRM_SORT_PARAM,
  CRM_SHORTCUT_PARAM.lead,
  CRM_SHORTCUT_PARAM.idle,
  CRM_SHORTCUT_PARAM.event,
  CRM_SHORTCUT_PARAM.junk,
] as const;

/** Вид таблицы по умолчанию (Сегодня, исход Все, сортировка по умолчанию). */
export function withCrmDefaultTableView(prev: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(prev);
  RESET_KEYS.forEach((key) => next.delete(key));
  dropManagerNone(next);
  return next;
}

/** Адрес кнопки: сброс к виду по умолчанию + её параметры. */
export function withCrmShortcut(prev: URLSearchParams, shortcut: CrmTableShortcut): URLSearchParams {
  const next = new URLSearchParams(prev);
  RESET_KEYS.forEach((key) => next.delete(key));
  next.set(CRM_PERIOD_PARAM.period, CRM_PERIOD_ALL);
  if (shortcut === 'junk') {
    next.set(CRM_SHORTCUT_PARAM.junk, FLAG_ON);
    dropManagerNone(next);
    return next;
  }
  if (shortcut === 'sales') {
    next.set(CRM_PERIOD_PARAM.outcome, 'won');
    dropManagerNone(next);
    return next;
  }
  next.set(CRM_PERIOD_PARAM.outcome, 'open');
  if (shortcut === 'unassigned') {
    next.set(CRM_SHORTCUT_PARAM.lead, FLAG_ON);
    next.set(CRM_FILTER_PARAM.manager, MANAGER_FILTER_NONE);
  } else {
    next.set(CRM_SHORTCUT_PARAM.idle, FLAG_ON);
    dropManagerNone(next);
  }
  return next;
}

/** Признаки кнопок быстрого выбора, которые ставят только кнопки. */
const SHORTCUT_FLAG_KEYS = [CRM_SHORTCUT_PARAM.lead, CRM_SHORTCUT_PARAM.idle, CRM_SHORTCUT_PARAM.junk] as const;

/**
 * KAN-883: адрес без активной кнопки быстрого выбора (перед ручной сменой периода или исхода):
 * junk, lead, idle и manager=none, если активна "Неразобранные" (выбранный вручную manager=none остаётся).
 */
export function withoutCrmShortcut(prev: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(prev);
  if (activeCrmShortcut(prev) === 'unassigned') dropManagerNone(next);
  SHORTCUT_FLAG_KEYS.forEach((key) => next.delete(key));
  return next;
}

/** Клик по кнопке: активная - вид по умолчанию, иначе её вид. */
export function toggleCrmShortcut(prev: URLSearchParams, shortcut: CrmTableShortcut): URLSearchParams {
  return activeCrmShortcut(prev) === shortcut ? withCrmDefaultTableView(prev) : withCrmShortcut(prev, shortcut);
}
